/**
 * Générateur — Il utilise les puissances de 10 d'exposants positifs ou négatifs.
 * Attendu ID : 136 | Niveau : 4ème | Domaine : Nombres (desc 11, O1)
 *
 * Recadrage 2026-09-23 (décision prof) : la carte « lois » (produit, quotient
 * et puissance d'une puissance de 10, texte à trous) est SUPPRIMÉE, et la
 * carte « egales » est recadrée sur les registres d'écriture. Les règles de
 * calcul sur les puissances de 10 ne sont pas au programme de 4e ; trois
 * sources concordent, vérifiées :
 *   1. le programme en vigueur (.claude/reference/programme-cycle4.md) met en
 *      4e « multiplier des puissances d'exposant entier naturel d'un même
 *      nombre », et en 3e « définir les puissances d'exposant négatif » et
 *      « multiplier et diviser des puissances » ; les automatismes de 3e, qui
 *      disent ce qui a été étudié en 4e, ne mentionnent ni la division ni
 *      l'exposant négatif ; la puissance d'une puissance n'apparaît nulle
 *      part au cycle 4 ;
 *   2. le cours de 4e du professeur ne contient aucune règle de calcul, ni
 *      produit, ni quotient, ni puissance d'une puissance : ses trois parties
 *      sont la définition d'une puissance, les puissances de 10 avec
 *      l'exposant négatif et les préfixes, puis la notation scientifique ;
 *   3. quand ce cours utilise un produit de puissances de 10, il ne l'écrit
 *      jamais comme une règle sur les exposants : pour ramener 147,3 × 10⁵ en
 *      notation scientifique il écrit 1,473 × 10² × 10⁵, puis revient aux
 *      écritures décimales, 100 × 100 000, pour conclure 10⁷.
 * Les règles de calcul vivent en 3e, carte « regles » du gen_201 (produit,
 * quotient, puissance d'une puissance). Quatre cartes restent ici, deux QCM :
 *
 *   Carte « ecrire » — 10ⁿ → écriture décimale (qcm_unique), n ∈ [-10 ; 10]
 *     distracteurs = exposant opposé + décalages d'un/deux zéros (valeurs
 *     numériquement distinctes garanties).
 *   Carte « forme » — écriture décimale → exposant (texte à trous, saisie), n ∈ [-10 ; 10]
 *     réponse = n signé (« -3 »), jamais l'écriture complète (litige d'espaces).
 *   Carte « relier » — écriture décimale ↔ 10ⁿ (relier) ; une ou deux sources
 *     d'exposant négatif sont données en fraction (1/1000 pour 10⁻³).
 *   Carte « egales » — écritures égales à 10ᵏ (qcm_multiple) : l'exercice de
 *     la page 3 du cours, registres décimal, produit répété de dix, fraction
 *     de numérateur un (1/1000, 1/10³, 1/(10 × 10 × 10)) ; pièges = erreurs
 *     nommées dans les mêmes registres, jamais une règle sur les exposants.
 *
 * Historique. Plan de cartes validé 2026-07-14 (cinq cartes, dont « lois » et
 * une « egales » bâtie sur 10ᵃ × 10ᵇ, 10ᵃ ÷ 10ᵇ, (10ᵃ)ᵇ). Revue 2026-09-22 :
 * plages [-10 ; 10] partout, boucles bornées avec repli, « relier » en forme
 * fractionnaire, parenthèses de consigne → phrases. Revue 2026-09-23 : tout
 * ce qui s'affiche comme puissance passe en KaTeX (puissTex et tex de
 * generators/utils.js, produits en \times) ; « forme » n'est plus une saisie
 * nue mais une phrase à trous en mode mathématique global, l'égalité entière
 * affichée et le trou à la place de l'exposant (« $1000 = 10^{{{3}}}$ »,
 * groupe protégé autour du trou, modèle gen_205), clé forme_texte →
 * forme_texte_trou_saisie. Composition 2026-09-23 (règle prof, cartes
 * relier) : « relier » passe de cinq paires à trois sources pour cinq cibles,
 * les trois bonnes et deux leurres (cibles_leurres de makeRelier), toujours
 * moins d'éléments à gauche qu'à droite ; les leurres sont les confusions de
 * la carte « ecrire », l'exposant opposé (10³ pour 1/1000) et l'exposant
 * décalé d'un (10⁵ pour 10 000), jamais une bonne cible ni deux fois la même
 * valeur ; l'énoncé prévient que des puissances restent orphelines.
 *
 * Tout par construction ; écritures décimales exactes (module nombres.js,
 * aucun flottant). Puissances en KaTeX (affichage), réponses signées en
 * ASCII « - ».
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeTexteTrou, makeRelier, fracTex, tex, puissTex,
} from '../../../utils';
import { dec10 } from './nombres';

const ATTENDU_ID = 136;

const L_ECRIRE = 'Écrire une puissance de 10 en écriture décimale';
const L_FORME = 'Écrire un nombre sous la forme 10ⁿ';
const L_RELIER = 'Associer écriture décimale et puissance de 10';
const L_EGALES = 'Reconnaître des expressions égales à une puissance de 10';

const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Groupe les milliers avec des espaces (AFFICHAGE seulement) : « 10000000 » → « 10 000 000 ».
const grp = (s) => {
    const neg = s.startsWith('-');
    let x = neg ? s.slice(1) : s;
    const [ent, dec] = x.split(',');
    const g = ent.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return (neg ? '-' : '') + (dec !== undefined ? `${g},${dec}` : g);
};

// La contrainte d'écriture, en phrase (jamais en parenthèse d'énoncé).
const PHRASE_SIGNE = '<p>Un exposant négatif s\'écrit avec le signe « - », par exemple -3.</p>';

// Fragment LaTeX d'une puissance de 10, à composer dans un tex() : « 10^{-3} ».
const p10 = (n) => `10^{${n}}`;
// Décimal français en LaTeX : « 0,001 » → « 0{,}001 » (sinon KaTeX espace la
// virgule). Sur une écriture SANS séparateur de milliers : les espaces sont
// ignorés en mode mathématique, les milliers sont posés au rendu
// (utils/milliers.js, espacerMilliersTex).
const nbTex = (s) => String(s).replace(',', '{,}');

// ═════════ Carte « ecrire » — 10ⁿ → écriture décimale ═════════

const genEcrireQcm = () => {
    const n = randInt(-10, 10);
    const correct = grp(dec10(n));
    const cand = new Set([correct]);
    // Distracteurs : exposant opposé, décalage d'un/deux zéros. Deux exposants
    // différents donnent deux écritures différentes : les candidats sont
    // distincts entre eux et de la clé ; seul l'opposé disparaît quand n = 0
    // (il reste alors quatre candidats pour trois places).
    [-n, n + 1, n - 1, n + 2, n - 2].forEach(m => cand.add(grp(dec10(m))));
    const distracteurs = [...cand].filter(c => c !== correct);
    const choix = [correct, ...shuffle(distracteurs).slice(0, 3)];
    const payload = makeQcm(choix, correct, `<p><b>Quelle est l'écriture décimale de ${puissTex(10, n)} ?</b></p>`, opts(L_ECRIRE));
    payload._meta.params = { n };
    return payload;
};

// ═════════ Carte « forme » — écriture décimale → exposant ═════════

const genFormeTrous = () => {
    const n = randInt(-10, 10);   // 21 valeurs distinctes (seuil d'unicité 20)
    // Ligne entière en mode mathématique (dollars aux deux bouts, cf.
    // hasMathLines) : l'exposant en trou est protégé par un groupe, 10^{{{n}}}
    // et jamais 10^{{n}} (KaTeX refuse un ^ suivi du markup du trou, cf. gen_205).
    const phrase = `$${nbTex(dec10(n))} = 10^{{{${n}}}}$`;
    const texte = `<p><b>Écris ce nombre sous la forme ${tex('10^{n}')} : complète l'exposant.</b></p>`
        + PHRASE_SIGNE;
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_FORME), mode: 'saisie' });
    payload._meta.params = { n };
    return payload;
};

// ═════════ Carte « relier » — écriture décimale ↔ 10ⁿ ═════════

const POOL_RELIER = [-6, -3, -2, -1, 0, 2, 3, 4, 6, 8];

// Composition 2026-09-23 (règle prof) : trois sources à gauche, cinq cibles à
// droite, les trois bonnes et deux leurres. Les leurres sont les deux
// confusions de la carte « ecrire », jamais du remplissage :
//   · exposant_oppose : 10³ pour 0,001 ou 1/1000, le signe de l'exposant
//     oublié ; l'opposé d'un exposant négatif d'abord, c'est la source en
//     fraction qui porte cette confusion ;
//   · decalage_un : 10⁵ pour 10 000, 10⁻² pour 0,001, les chiffres comptés
//     à la place des zéros.
// Un leurre n'est jamais une bonne cible ni un autre leurre : filtre sur la
// chaîne affichée (puissTex), injective en l'exposant. Pour trois exposants
// distincts, les décalages d'un cran hors sélection sont au moins deux (le
// plus petit moins un, le plus grand plus un) et l'opposé retenu ne peut en
// occuper qu'un : la carte a toujours ses deux leurres, sans boucle.
const genRelier = () => {
    let expos = shuffle(POOL_RELIER).slice(0, 3);
    // Au moins un exposant négatif parmi les trois, sinon rien à écrire en fraction
    // (repli déterministe : -3 n'est pas dans une sélection sans négatif).
    if (!expos.some(n => n < 0)) expos = [-3, ...expos.slice(1)];
    // Une ou deux sources d'exposant négatif en écriture fractionnaire (1/1000
    // pour 10⁻³) : l'intitulé de l'attendu couvre les deux écritures.
    const negatifs = shuffle(expos.filter(n => n < 0));
    const enFraction = new Set(negatifs.slice(0, randInt(1, 2)));
    const pairs = expos.map(n => ({
        source: enFraction.has(n) ? fracTex(1, Number(dec10(-n))) : grp(dec10(n)),
        target: puissTex(10, n),
    }));
    const pris = new Set(pairs.map(p => p.target));
    const leurres = [];
    const ajouter = (n, code) => {
        const target = puissTex(10, n);
        if (leurres.length >= 2 || pris.has(target)) return;
        pris.add(target);
        leurres.push({ target, code });
    };
    const opposes = [...negatifs.map(n => -n), ...shuffle(expos.filter(n => n > 0)).map(n => -n)];
    const decales = shuffle(expos.flatMap(n => [n + 1, n - 1]));
    const oppose = opposes.find(n => !pris.has(puissTex(10, n)));
    if (oppose !== undefined) ajouter(oppose, 'exposant_oppose');
    decales.forEach(n => ajouter(n, 'decalage_un'));
    const payload = makeRelier(pairs,
        '<p><b>Relie chaque nombre à la puissance de 10 qui lui est égale.</b> Attention : certaines puissances de 10 ne correspondent à aucun nombre.</p>',
        { ...opts(L_RELIER), cibles_leurres: leurres });
    payload._meta.params = {
        expos,
        enFraction: [...enFraction],
        distracteurs: Object.fromEntries(leurres.map(l => [l.target, l.code])),
    };
    return payload;
};

// ═════════ Carte « egales » — écritures égales à 10ᵏ (QCM multiple) ═════════
//
// L'exercice de la page 3 du cours : quelles écritures valent une puissance
// de 10 donnée. Les registres d'ÉCRITURE du cours, et eux seuls ; aucun choix
// ne demande d'ajouter ou de multiplier deux exposants :
//   · decimal   : 0,001 pour 10⁻³, 1 000 000 pour 10⁶ ;
//   · produit   : 10 × 10 × 10 pour 10³, le produit répété de dix ;
//   · frac_dec  : 1/1000 pour 10⁻³, la fraction de numérateur un ;
//   · frac_puis : 1/10³ pour 10⁻³ ;
//   · frac_prod : 1/(10 × 10 × 10) pour 10⁻³, le dénominateur en produit de
//                 dix, écrit tel quel dans le cours.
// Un exposant positif a deux bonnes écritures (decimal, produit), un exposant
// négatif en a trois ou quatre ; 1/1000, 0,001 et 1/10³ valent tous 10⁻³ et
// ce sont des bonnes réponses, jamais des pièges.
//
// Les pièges sont des erreurs nommées, dans les mêmes registres. La VALEUR
// est tirée avant l'écriture : les exposants -k, k + 1 et k - 1 sont distincts
// entre eux et de k dès que |k| ≥ 2, donc aucun piège ne vaut la clé ni un
// autre piège, quel que soit le registre. Les quatre familles :
//   · signe_oppose     : 10 × 10 × 10 pour 10⁻³, 1/1000 pour 10³ (registres
//                        structurés, valeur 10^-k) ;
//   · un_suivi_precede : 1 000 pour 10⁻³, 0,001 pour 10³ (registre décimal,
//                        valeur 10^-k, le 1 suivi de n zéros pris pour le 1
//                        précédé de n zéros) ;
//   · decalage_un      : 10 × 10 pour 10³, 1/10⁴ pour 10⁻³ (registres
//                        structurés, valeur 10^(k ± 1)) ;
//   · zeros_comptes    : 10 000 ou 100 pour 10³, 0,0001 ou 0,01 pour 10⁻³
//                        (registre décimal, le nombre de zéros compté de
//                        travers, valeur 10^(k ± 1)).
// k ∈ [-9 ; -2] ∪ [2 ; 6] : jamais 0 ni ±1 (10¹ = 10 n'a qu'une écriture ;
// 10⁻¹ = 0,1 = 1/10 se lit dans « relier ») ; tout exposant affiché reste
// dans [-10 ; 9] et un produit affiché a au plus sept facteurs (le registre
// décimal, lui, existe toujours).

const FACTEURS_MAX = 7;
const POOL_EGALES = [-9, -8, -7, -6, -5, -4, -3, -2, 2, 3, 4, 5, 6];
const produitDix = (n) => Array.from({ length: n }, () => '10').join(' \\times ');

// Écriture de 10ᵉ dans un registre, ou null quand le registre ne s'y prête
// pas : un produit a de deux à sept facteurs, une fraction un exposant
// négatif, « 10¹ » au dénominateur n'est pas une écriture du cours.
const REGISTRES = {
    decimal: (e) => tex(nbTex(dec10(e))),
    produit: (e) => (e >= 2 && e <= FACTEURS_MAX ? tex(produitDix(e)) : null),
    frac_dec: (e) => (e <= -1 ? tex(`\\dfrac{1}{${dec10(-e)}}`) : null),
    frac_puis: (e) => (e <= -2 ? tex(`\\dfrac{1}{${p10(-e)}}`) : null),
    frac_prod: (e) => (e <= -2 && -e <= FACTEURS_MAX ? tex(`\\dfrac{1}{${produitDix(-e)}}`) : null),
};

// Toutes les écritures disponibles de 10ᵉ : { registre, expr }.
const ecrituresDe = (e) => Object.entries(REGISTRES)
    .map(([registre, f]) => ({ registre, expr: f(e) }))
    .filter(c => c.expr !== null);

const genEgalesQcmMultiple = () => {
    const k = randomChoice(POOL_EGALES);
    const bonsPossibles = ecrituresDe(k);
    const nbBons = Math.min(randInt(2, 3), bonsPossibles.length);
    const bons = shuffle(bonsPossibles).slice(0, nbBons).map(c => c.expr);
    const nbMauvais = 5 - nbBons;
    // Un piège par valeur, la valeur d'abord, le registre ensuite : décimal ou
    // structuré à égalité quand les deux existent, décimal seul sinon.
    const mauvais = shuffle([-k, k + 1, k - 1]).slice(0, nbMauvais).map(m => {
        const formes = ecrituresDe(m);
        const decimale = formes.find(c => c.registre === 'decimal');
        const structurees = formes.filter(c => c.registre !== 'decimal');
        const choisi = structurees.length && randomChoice(['decimal', 'structure']) === 'structure'
            ? randomChoice(structurees) : decimale;
        const code = choisi.registre === 'decimal'
            ? (m === -k ? 'un_suivi_precede' : 'zeros_comptes')
            : (m === -k ? 'signe_oppose' : 'decalage_un');
        return { expr: choisi.expr, code };
    });
    // La consigne dit combien de réponses cocher (règle du format qcm_multiple).
    const nombre = nbBons === 2 ? 'deux' : 'trois';
    const texte = `<p><b>Sélectionne les ${nombre} écritures égales à ${puissTex(10, k)}.</b></p>`;
    const payload = makeQcm([...bons, ...mauvais.map(p => p.expr)], bons, texte, opts(L_EGALES), true);
    payload._meta.params = {
        k,
        bons,
        distracteurs: Object.fromEntries(mauvais.map(p => [p.expr, p.code])),
    };
    return payload;
};

// Contrat des distracteurs des deux QCM (convention du dépôt, gen_603 =
// modèle) : données pures, jamais lues à l'exécution ; un relecteur vérifie
// en trente secondes que le code respecte les arbitrages.
export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'chaque distracteur est une erreur NOMMÉE d\'élève, jamais du remplissage ; les codes sont portés dans _meta.params.distracteurs',
        'un distracteur ne vaut jamais la clé, ni un autre distracteur : la VALEUR (l\'exposant) est tirée avant l\'écriture, distincte de la clé et des autres',
        'aucun choix ne demande d\'ajouter ou de multiplier deux exposants (décision prof 2026-09-23 : les règles de calcul sont hors programme de 4e, elles vivent en 3e dans la carte « regles » du gen_201)',
        'SUPPRIMÉES par arbitrage 2026-09-23, ne pas réintroduire : produit_des_exposants (10² × 10⁷ proposé pour 10¹⁴) et toute écriture 10ᵃ × 10ᵇ, 10ᵃ ÷ 10ᵇ ou (10ᵃ)ᵇ, bonne ou fausse',
    ],
    ecrire_qcm_unique: {
        support: 'n ∈ [-10 ; 10], quatre choix en écriture décimale',
        fixes: {
            exposant_oppose: 'écriture décimale de 10^-n (absent pour n = 0)',
            decalage: 'écritures décimales de 10^(n ± 1) et 10^(n ± 2), trois tirées parmi les candidats distincts',
        },
    },
    egales_qcm_multiple: {
        support: 'k ∈ [-9 ; -2] ∪ [2 ; 6] ; cinq choix, deux ou trois bons (deux pour k > 0) ; registres decimal, produit, frac_dec, frac_puis, frac_prod',
        fixes: {
            signe_oppose: 'valeur 10^-k en registre structuré : 10 × 10 × 10 pour 10⁻³, 1/1000 ou 1/10³ ou 1/(10 × 10 × 10) pour 10³',
            un_suivi_precede: 'valeur 10^-k en registre décimal : 1 000 pour 10⁻³, 0,001 pour 10³',
            decalage_un: 'valeur 10^(k ± 1) en registre structuré : 10 × 10 pour 10³, 1/10⁴ pour 10⁻³',
            zeros_comptes: 'valeur 10^(k ± 1) en registre décimal : 10 000 ou 100 pour 10³, 0,0001 ou 0,01 pour 10⁻³',
        },
        rotation: 'un piège par valeur parmi -k, k + 1, k - 1, deux ou trois tirées ; pour chaque valeur, registre décimal ou structuré à égalité quand les deux existent ; le registre structuré manque pour un produit d\'un seul facteur ou de plus de sept, le décimal existe toujours',
    },
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    ecrire_qcm_unique: genEcrireQcm,
    forme_texte_trou_saisie: genFormeTrous,
    relier_relier: genRelier,
    egales_qcm_multiple: genEgalesQcmMultiple,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte_trou_saisie: 'Saisie trous', relier: 'Relier', qcm_multiple: 'QCM Multi',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

// etapes : nombre d'étapes de raisonnement de la carte, validé par le prof le
// 2026-10-03 (chantier groupes-differencies) ; sert aux exercices complexes du groupe Fort.
export const CATEGORIES = [
    { id: 'ecrire', label: L_ECRIRE, etapes: 1, formatsList: buildFormats('ecrire') },
    { id: 'forme', label: L_FORME, etapes: 1, formatsList: buildFormats('forme') },
    { id: 'relier', label: L_RELIER, etapes: 1, formatsList: buildFormats('relier') },
    { id: 'egales', label: L_EGALES, etapes: 2, formatsList: buildFormats('egales') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
